const DEFAULT_REDIRECT_PATH = '/';

const DISALLOWED_REDIRECT_PREFIXES = [
	'/login',
	'/signup',
	'/auth-callback',
	'/test-signin',
	'/test-sign-in',
	'/test-signup',
	'/test-sign-up'
];

const getSafeWindowOrigin = () => {
	if (typeof window !== 'undefined' && window.location?.origin) {
		return window.location.origin;
	}
	return 'http://localhost';
};

const safeSessionStorageGet = (key) => {
	try {
		return sessionStorage.getItem(key);
	} catch (_) {
		return null;
	}
};

const safeSessionStorageRemove = (key) => {
	try {
		sessionStorage.removeItem(key);
	} catch (_) {
		// ignore storage failures in restricted contexts
	}
};

const getHashSearchParams = (hash = '') => {
	if (!hash) return new URLSearchParams();

	const withoutHash = hash.startsWith('#') ? hash.slice(1) : hash;
	const queryIndex = withoutHash.indexOf('?');
	if (queryIndex !== -1) {
		return new URLSearchParams(withoutHash.slice(queryIndex + 1));
	}

	const normalized = withoutHash.replace(/^\//, '');
	if (!normalized.includes('=')) {
		return new URLSearchParams();
	}
	return new URLSearchParams(normalized);
};

const isSafeInAppPath = (value) => {
	if (typeof value !== 'string') return false;

	const trimmed = value.trim();
	if (!trimmed.startsWith('/')) return false;
	if (trimmed.startsWith('//')) return false;

	return !DISALLOWED_REDIRECT_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
};

const extractPathFromSameOriginUrl = (urlValue) => {
	if (typeof urlValue !== 'string' || !urlValue.trim()) return null;

	try {
		const origin = getSafeWindowOrigin();
		const parsed = new URL(urlValue, origin);
		if (parsed.origin !== origin) return null;

		return `${parsed.pathname}${parsed.search}${parsed.hash}`;
	} catch (_) {
		return null;
	}
};

const resolveCandidate = (value, depth = 0) => {
	if (!value || depth > 2) return null;

	if (isSafeInAppPath(value)) {
		return value.trim();
	}

	const asUrlPath = extractPathFromSameOriginUrl(value);
	if (!asUrlPath) return null;

	const nestedParams = new URLSearchParams(new URL(asUrlPath, getSafeWindowOrigin()).search);
	const nestedRedirect = nestedParams.get('redirectTo');
	if (nestedRedirect) {
		const resolvedNested = resolveCandidate(nestedRedirect, depth + 1);
		if (resolvedNested) return resolvedNested;
	}

	return isSafeInAppPath(asUrlPath) ? asUrlPath : null;
};

export const resolveAuthDestinationFromLocation = (locationLike, fallback = DEFAULT_REDIRECT_PATH) => {
	try {
		const searchParams = new URLSearchParams(locationLike?.search || '');
		const hashParams = getHashSearchParams(locationLike?.hash || '');

		const candidates = [
			searchParams.get('redirectTo'),
			hashParams.get('redirectTo'),
			searchParams.get('after_sign_in_url'),
			hashParams.get('after_sign_in_url'),
			searchParams.get('after_sign_up_url'),
			hashParams.get('after_sign_up_url')
		];

		for (const candidate of candidates) {
			const resolved = resolveCandidate(candidate);
			if (resolved) return resolved;
		}
	} catch (_) {
		// fall through to fallback
	}

	return isSafeInAppPath(fallback) ? fallback : DEFAULT_REDIRECT_PATH;
};

export const buildAuthCallbackRedirectUrl = (destinationPath) => {
	const safeDestination = isSafeInAppPath(destinationPath) ? destinationPath : DEFAULT_REDIRECT_PATH;
	return `/auth-callback?redirectTo=${encodeURIComponent(safeDestination)}`;
};

export const getStoredClerkCallbackUrl = () => {
	return safeSessionStorageGet('clerk_auth_callback_url');
};

export const clearStoredClerkCallbackUrl = () => {
	safeSessionStorageRemove('clerk_auth_callback_url');
};
